import React, { useCallback, useEffect, useMemo, useState, useRef, useContext } from 'react';
import { VariableSizeList as List } from 'react-window';
import { AutoSizer } from 'react-virtualized';
import TimelineItem from './TimelineItem';
import { RootState } from 'store';
import { useSelector, useDispatch } from 'react-redux';
import { decryptAES_CBC } from 'hooks/useImageDecrypt';

import { getVideoByHashId } from 'store/decryptImagesSlice';
import { ModalContext } from 'context/ModalContext';
import { MODAL_ANIMATIONS, MODAL_TYPES } from 'components/Modal/constants';
import { removeTimeline } from 'api/timelineAPI';
import { seekPlayerTime } from 'store/secondaryViewSlice';

type ItemSizeMap = Map<number, number>;

interface ISearchResults {
  results: any[];
  searchText: string;
  videoHashId: string;
  channelHashId: string;
  videoShotId: string;
  aesKeyId: string;
  decryptionKeys: any;
  people: any;
  navigateVideoPage: any;
  isResizing: boolean;
}

function SearchResults({
  results,
  searchText,
  videoHashId,
  channelHashId,
  aesKeyId,
  decryptionKeys,
  videoShotId,
  people,
  navigateVideoPage,
  isResizing
}: ISearchResults) {
  const listRef = useRef<List>(null);
  const itemSizeMap = useRef<ItemSizeMap>(new Map());
  const userData = useSelector((state: RootState) => state.user.user);
  const { showModal } = useContext(ModalContext);
  const dispatch = useDispatch();
  const secondaryViewSize = useRef();
  const [hoveredId, setHoveredId] = useState(null as any);

  const timelines = useMemo(() => {
    return (
      results
        ?.map((item) => item.timeline || []) // Safely map and handle missing timeline
        .reduce((acc, curr) => acc.concat(curr), []) || []
    );
  }, [results]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(0, false);
    }
  }, [timelines, listRef.current]);

  const handleEdit = (id: string) => {
    if (editingId !== null && editingId !== id) {
      showModal(MODAL_TYPES.edit_summary, {
        animation: MODAL_ANIMATIONS.fade,
        channelHashId,
        setEditingId,
        editingId: id
      });
    } else {
      setEditingId(id);
    }
  };

  const [editingId, setEditingId] = useState<null | string>(null);
  const isEditing = useCallback(
    (hashId) => {
      return editingId === hashId;
    },
    [editingId]
  );

  const getItemSize = (index: number) => {
    return itemSizeMap.current.get(index) || 50; // default size of 50, item will be resized dynamically
  };

  const setItemSize = (index: number, size: number) => {
    const currentSize = itemSizeMap.current.get(index);
    if (currentSize !== size && listRef.current) {
      // Ensure listRef.current is not null
      itemSizeMap.current.set(index, size + 4); // add 4px for height to make room
      // Optionally, you can force the list to recompute its layout without triggering a re-render
      // This prevents the scrolling behavior from resetting to the top
      listRef.current.resetAfterIndex(index, true);
    }
  };

  const Row = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    const timeline = timelines[index];

    const isEdited = isEditing(timeline?.hash_id);

    const itemRef = useCallback(
      (node) => {
        if (node !== null) {
          const height = node.getBoundingClientRect().height;
          const currentSize = itemSizeMap.current.get(index);

          // Only update if the size has changed to prevent unnecessary renders
          if ((height && !currentSize) || currentSize !== height) {
            setItemSize(index, height);
          }
        }
      },
      [index]
    );

    let itemStyle = {
      ...style
    };

    if (isEdited) {
      itemStyle = {
        ...itemStyle,
        zIndex: 999,
        height: 'auto',
        marginBottom: '10px',
        backgroundColor: '#fff'
      };
    } else {
      itemStyle = {
        ...itemStyle
      };
    }

    const handleSpeaker = (hashId: string) => {
      // Update speaker
      const timeline = timelines.find((item: any) => item.hash_id == hashId);
      showModal(MODAL_TYPES.add_speaker, {
        people: people,
        sentence: timeline,
        speaker: timeline.user_info.hash_id,
        video_info_hash_id: videoHashId,
        videoshot_hash_id: videoShotId,
        sentence_hash_id: timeline.hash_id,
        inTimeline: true
      });
    };

    const handleDelete = (timelineHashId: string) => {
      const confirmDelete = async () => {
        await removeTimeline(videoHashId, videoShotId, timelineHashId);
      };

      showModal(MODAL_TYPES.remove_summary, {
        animation: MODAL_ANIMATIONS.fade,
        modalMessage: {
          title: 'Deleting Timeline',
          text: 'You are about to to delete a timeline.  Click "Confirm to delete" button.'
        },
        onConfirm: confirmDelete
      });
    };

    const navigateToVideo = (time: any) => {
      const isActivePlayer = sessionStorage.getItem('playerActive');
      if (isActivePlayer === 'true') {
        dispatch(seekPlayerTime({ channelHashId: channelHashId, time: time }));
      } else {
        navigateVideoPage(time);
      }
    };

    return (
      <div style={itemStyle} className={`relative w-full ${!isResizing ? 'hover:bg-[#f8f8f8]' : ''}`}>
        <div ref={itemRef} className="py-1">
          <TimelineItem
            timeline={timeline}
            displayAvatar={true}
            userData={userData}
            navigateToVideo={navigateToVideo}
            user={{
              avatar: timeline.user_info?.avatar ? timeline.user_info.avatar : timeline.active_speaker_thumbnail_url,
              display_name: timeline.user_info?.display_name
                ? timeline.user_info?.display_name
                : timeline.user_info?.name || 'Unknown',
              email: timeline.user_info?.email,
              name: timeline.user_info?.name,
              id: timeline.user_info?.user_id
            }}
            aesKeyId={aesKeyId}
            decryptionKeys={decryptionKeys}
            videoHashId={videoHashId}
            channelId={channelHashId}
            isEditing={editingId === timeline.hash_id}
            videoShotId={videoShotId}
            handleEdit={() => {
              handleEdit(timeline.hash_id);
            }}
            handleCancelEdit={() => {
              setEditingId(null);
            }}
            handleSpeaker={() => handleSpeaker(timeline.hash_id)}
            onDelete={() => handleDelete(timeline.hash_id)}
            searchText={searchText}
            isExternal={false}
            secondaryViewSize={secondaryViewSize.current}
            isResizing={isResizing}
            hoveredId={hoveredId}
            handleHoveredId={() => {
              setHoveredId(timeline.hash_id);
            }}
          ></TimelineItem>
        </div>
      </div>
    );
  };

  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const dimensionsRef = useRef({ width: 0, height: 0 });
  useEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(1, true); // Reset all items
    }
  }, [dimensions]);

  return (
    <div className={`h-full w-full flex flex-col flex-1 overflow-y-auto pb-2 pt-2`}>
      <AutoSizer>
        {({ height, width }: any) => {
          secondaryViewSize.current = width;
          if (dimensionsRef.current.width !== width || dimensionsRef.current.height !== height) {
            dimensionsRef.current = { width, height };
            // Schedule the state update outside the render phase
            requestAnimationFrame(() => setDimensions({ width, height }));
          }
          return (
            <List ref={listRef} height={height || 0} width={width} itemCount={timelines?.length} itemSize={getItemSize}>
              {Row}
            </List>
          );
        }}
      </AutoSizer>
    </div>
  );
}

export default SearchResults;
